import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { useToast } from './ui/use-toast'
import { Input } from './ui/input'
import { User, Briefcase, DollarSign, Calendar, Users, ShieldAlert, Search, Building, BarChart } from 'lucide-react'

interface OrganizationData {
  id: string
  name: string
  owner: {
    first_name: string
    last_name: string
    email: string
  } | null
  subscription: {
    plan: string
    status: string
    start_date: string
  } | null
  doctor_count: number
  total_payments: number
}

interface AdminDashboardClientProps {
  organizations: OrganizationData[]
}

const StatCard = ({ title, value, icon, description }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
);

export default function AdminDashboardClient({ organizations: initialOrganizations }: AdminDashboardClientProps) {
  const [organizations, setOrganizations] = useState(initialOrganizations)
  const [loading, setLoading] = useState<Record<string, boolean>>({})
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState('')

  const stats = useMemo(() => {
    const totalOrgs = initialOrganizations.length;
    const activeSubs = initialOrganizations.filter(org => org.subscription?.status === 'active').length;
    const totalRevenue = initialOrganizations.reduce((acc, org) => acc + (org.total_payments || 0), 0);
    return { totalOrgs, activeSubs, totalRevenue };
  }, [initialOrganizations]);

  // TODO: Implement deactivateSubscription for Vite/Supabase
  const handleDeactivate = async (orgId: string) => {
    setLoading(prev => ({ ...prev, [orgId]: true }))
    // Implement actual deactivation logic here
    toast({
      title: 'Success',
      description: 'Subscription has been deactivated.',
    })
    setOrganizations(orgs => orgs.map(org => org.id === orgId ? { ...org, subscription: { ...org.subscription, status: 'inactive' } } : org))
    setLoading(prev => ({ ...prev, [orgId]: false }))
  }

  const filteredOrganizations = organizations.filter(org => {
    const ownerName = `${org.owner?.first_name || ''} ${org.owner?.last_name || ''}`.toLowerCase()
    const orgName = org.name.toLowerCase()
    const lowercasedSearchTerm = searchTerm.toLowerCase()
    return orgName.includes(lowercasedSearchTerm) || ownerName.includes(lowercasedSearchTerm)
  })

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard 
          title="Total Organizations" 
          value={stats.totalOrgs}
          description="The total number of organizations on the platform."
          icon={<Building className="h-4 w-4 text-muted-foreground" />} 
        />
        <StatCard 
          title="Active Subscriptions" 
          value={stats.activeSubs} 
          description="The number of organizations with an active subscription."
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard 
          title="Total Revenue" 
          value={`$${stats.totalRevenue.toFixed(2)}`}
          description="The total revenue generated from all organizations."
          icon={<BarChart className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder="Search by organization name or owner..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full max-w-lg pl-10 pr-4 py-2 border rounded-lg"
        />
      </div>

      {filteredOrganizations.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredOrganizations.map(org => (
            <Card key={org.id} className="flex flex-col bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden transform transition-all hover:scale-105">
              <CardHeader className="bg-gray-50 dark:bg-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-800 dark:text-white">{org.name}</CardTitle>
                  <Badge variant={org.subscription?.status === 'active' ? 'success' : 'destructive'}>
                    {org.subscription?.status ?? 'None'}
                  </Badge>
                </div>
                <CardDescription className="text-sm text-gray-500 dark:text-gray-400">{org.owner ? `${org.owner.first_name} ${org.owner.last_name}` : 'No Owner'}</CardDescription>
              </CardHeader>
              <CardContent className="p-4 flex-grow space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <User className="h-4 w-4 text-blue-500" />
                  <span>{org.owner?.email ?? 'N/A'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <Briefcase className="h-4 w-4 text-blue-500" />
                  <span>Plan: {org.subscription?.plan ?? 'N/A'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span>
                    Since: {org.subscription?.start_date ? new Date(org.subscription.start_date).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3 grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-xs text-muted-foreground">Doctors</p>
                      <p className="font-bold text-md">{org.doctor_count}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue</p>
                      <p className="font-bold text-md">${(org.total_payments || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gray-50 dark:bg-gray-700 p-3">
                <Button
                  className="w-full text-white bg-red-600 hover:bg-red-700"
                  variant="destructive"
                  onClick={() => handleDeactivate(org.id)}
                  disabled={loading[org.id] || org.subscription?.status !== 'active'}
                >
                  <ShieldAlert className="mr-2 h-4 w-4" />
                  {loading[org.id] ? 'Deactivating...' : 'Deactivate'}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-20 border-2 border-dashed rounded-lg bg-gray-50 dark:bg-gray-800">
          <Search className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-2xl font-semibold text-gray-800 dark:text-white">No Organizations Found</h3>
          <p className="text-muted-foreground mt-2">Your search for "{searchTerm}" did not match any organizations.</p>
        </div>
      )}
    </div>
  )
} 